import React, { useState, useMemo, useEffect, useCallback, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Card, Row, Col, Modal, Form, Spinner, Button } from 'react-bootstrap';
import { FaMapMarkerAlt, FaPlayCircle } from 'react-icons/fa';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { GoogleMapsProvider } from '../../../../../providers/GoogleMapsProvider';
import { CamarasService } from '../../../../../services/apiService';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { FixedSizeList as List } from 'react-window';

// Carga diferida de componentes pesados
const GoogleMapSelector = lazy(() => import('../../../../../components/common/google-maps-selector/GoogleMapSelector'));
const CamaraVisor = lazy(() => import('../../../../../components/camaras/CamaraVisor'));

dayjs.extend(customParseFormat);
dayjs.locale('es');

const ROW_HEIGHT = 65;

const customStyles = `
.list-striped .alert-row:nth-child(odd) {
  background-color: rgba(250, 250, 250, 0.9);
}
.list-striped .alert-row:nth-child(even) {
  background-color: white;
}
.list-striped .alert-row:hover {
  background-color: rgba(245, 245, 245, 1);
}
.table-header {
  background-color: var(--falcon-200);
  font-weight: 600;
  font-size: 0.83rem;
  position: sticky;
  top: 0;
  z-index: 1;
}
.alert-row {
  padding: 0.5rem 0.5rem;
  font-size: 0.83rem;
  transition: background-color 0.15s ease;
}
.avatar-name.rounded-circle {
  display: flex;
  align-items: center;
  justify-content: center;
}
`;

const formatFecha = (fechaStr) => {
    if (!fechaStr || typeof fechaStr !== 'string') return 'N/A';
    try {
        return dayjs(fechaStr, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY hh:mm:ss A');
    } catch (error) {
        return 'N/A';
    }
};

// Worker para exportación de datos
const exportWorker = () => {
    return new Worker(
        URL.createObjectURL(
            new Blob([
                `
                self.onmessage = function(e) {
                    const { data, title } = e.data;
                    const formatted = data.map(item => ({
                        Fecha: item.fecha,
                        Alerta: item.alerta,
                        Vehiculo: item.no_economico,
                        Chofer: item.chofer || 'N/A',
                        Geocerca: item.geocerca || 'N/A'
                    }));
                    self.postMessage({ formatted, title });
                }
                `
            ], { type: 'application/javascript' })
        )
    );
};


const MemoizedRow = React.memo(({ item, index, style, handleShowMap, handleShowVideoModal, isGroup }) => {
    return (
        <div
            className={`alert-row d-flex ${index % 2 === 0 ? 'even-row' : 'odd-row'}`}
            style={{
                ...style,
                alignItems: 'center',
                borderBottom: '1px solid #f5f5f5',
            }}
        >
            <div style={{ width: '15%', fontWeight: '500' }}>{formatFecha(item.fecha)}</div>
            <div style={{ width: '25%' }}>
                <div className="d-flex align-items-center">
                    <div className="avatar avatar-xl me-3">
                        <div className="avatar-name rounded-circle"
                            style={{
                                backgroundColor: `${item.color || '#888888'}33`,
                                color: item.color || '#888888',
                                border: `2px solid ${item.color || '#888888'}`
                            }}>
                            <span className="fs-9">
                                {isGroup ? (item.alerta_real?.[0] || "") : (item.alerta?.[0] || "")}
                            </span>
                        </div>
                    </div>
                    {isGroup ? (
                        <div>
                            <strong>{item.alerta_real}</strong>
                            <br />
                            <span>{item.alerta}</span>
                        </div>
                    ) : (
                        <div>
                            <strong>{item.alerta}</strong>
                        </div>
                    )}
                </div>
            </div>
            <div style={{ width: '12%' }}>{item.no_economico}</div>
            <div style={{ width: '15%' }}>{item.chofer || 'N/A'}</div>
            <div style={{ width: '8%', textAlign: 'center' }}>
                {item.vehiculo_con_camara && item.camara_en_linea ? (
                    <FaPlayCircle
                        size={20}
                        className="text-danger cursor-pointer"
                        onClick={() => handleShowVideoModal(item)}
                        style={{ cursor: 'pointer' }}
                    />
                ) : (
                    <span className="text-muted">N/A</span>
                )}
            </div>
            <div style={{ width: '15%' }}>{item.geocerca || 'N/A'}</div>
            <div style={{ width: '10%', textAlign: 'center' }}>
                <Button size="sm" className="btn-light rounded-5 p-1" onClick={() => handleShowMap(item.latitud, item.longitud)}>
                    <FaMapMarkerAlt className="text-danger" />
                </Button>
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    // Optimización: comparar solo lo necesario
    return prevProps.item.id === nextProps.item.id && 
           prevProps.index === nextProps.index && 
           prevProps.isGroup === nextProps.isGroup;
});


const AlertaTableList = ({
    data = [],
    total = 0,
    page = 1,
    perPage = 100,
    totalPages = 1,
    onPageChange,
    onPageSizeChange,
    onSearch,
    searchValue = '',
    title,
    isGroup,
    isExporting = false,
    onExport
}) => {
    // Estados para modales y video
    const [showMapModal, setShowMapModal] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState({ lat: null, lng: null });
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [videoChecks, setVideoChecks] = useState([false, false, false, false]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [urlVideo, setUrlVideo] = useState(["", "", "", ""]);
    const [consultandoCamara, setConsultandoCamara] = useState([false, false, false, false]);
    const videoIds = useMemo(() => ["canal-1", "canal-2", "canal-3", "canal-4"], []);
    const MySwal = withReactContent(Swal);

    // Handlers
    const handleShowMap = useCallback((lat, lng) => {
        setSelectedLocation({ lat, lng });
        setShowMapModal(true);
    }, []);

    const handleShowVideoModal = useCallback((vehicle) => {
        setSelectedVehicle(vehicle);
        setShowVideoModal(true);
    }, []);

    const handleToggleVideo = useCallback((event, index) => {
        let _urlVideo = '';
        if (event.target.checked) {
            setConsultandoCamara(prev => {
                const items = [...prev];
                items[index] = true;
                return items;
            });
            if (selectedVehicle && selectedVehicle.id_camara) {
                CamarasService.getHistorialDeVisualizacion({
                    id_camara: selectedVehicle.id_camara,
                    canal: index,
                    fecha_inicio_ticks: selectedVehicle.fecha,
                    fecha_fin_ticks: selectedVehicle.fecha
                }).then(resp => {
                    if (resp.statusCode === 200 && resp.data !== null) {
                        _urlVideo = resp.data.url;
                        setUrlVideo(prev => {
                            const items = [...prev];
                            items[index] = _urlVideo;
                            return items;
                        });
                    } else {
                        setVideoChecks(prev => {
                            const newChecks = [...prev];
                            newChecks[index] = false;
                            return newChecks;
                        });
                        MySwal.fire({
                            icon: 'error',
                            text: resp.data ? 'No se pudo obtener la información' : resp.message,
                            showConfirmButton: true
                        });
                    }
                    setConsultandoCamara(prev => {
                        const items = [...prev];
                        items[index] = false;
                        return items;
                    });
                });
            }
        } else {
            setUrlVideo(prev => {
                const items = [...prev];
                items[index] = '';
                return items;
            });
        }
        setVideoChecks(prev => {
            const newChecks = [...prev];
            newChecks[index] = !newChecks[index];
            return newChecks;
        });
    }, [selectedVehicle, MySwal]);

    const limpiarModal = useCallback(() => {
        setShowVideoModal(false);
        setVideoChecks([false, false, false, false]);
        setUrlVideo(["", "", "", ""]);
    }, []);

    // Exportación con Web Worker
    const handleExportar = useCallback(() => {
        if (typeof onExport === 'function') {
            onExport();
            return;
        }
        // Default export: exporta la página actual
        const worker = exportWorker();
        worker.onmessage = (e) => {
            const { formatted, title } = e.data;
            import('xlsx').then(XLSX => {
                const ws = XLSX.utils.json_to_sheet(formatted);
                const wb = XLSX.utils.book_new();
                XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: "A1" });
                if(!ws['!merges']) ws['!merges'] = [];
                ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } });
                const colWidths = [];
                for (let i = 0; i < 5; i++) colWidths.push({ wch: 15 });
                ws['!cols'] = colWidths;
                XLSX.utils.book_append_sheet(wb, ws, "Alertas");
                XLSX.writeFile(wb, "ReporteDeAlertas.xlsx");
            });
            worker.terminate();
        };
        worker.postMessage({ data, title });
    }, [data, title, onExport]);

    // Render
    return (
        <GoogleMapsProvider>
            <Card className="h-100">
                <Card.Header>
                    <Row className="d-flex align-items-center justify-content-between">
                        <Col md={6}>
                            <h6 className="mb-0 text-nowrap py-2 py-xl-2">{title}</h6>
                            <span className="text-muted fs-10">Total: {(total).toLocaleString('es-MX')}</span>
                        </Col>
                        <Col md={6} className="d-flex justify-content-end align-items-center">
                            <Form.Control
                                type="text"
                                placeholder="Buscar alertas..."
                                value={searchValue}
                                onChange={e => onSearch && onSearch(e.target.value)}
                                style={{ maxWidth: 200, marginRight: 8 }}
                                size="sm"
                            />
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="me-3 ms-3"
                                onClick={handleExportar}
                                disabled={isExporting || data.length === 0}
                            >
                                {isExporting ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-1"
                                        />
                                        Exportando...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faExternalLinkAlt} /> Exportar
                                    </>
                                )}
                            </Button>
                        </Col>
                    </Row>
                </Card.Header>

                <Card.Body className="p-0">
    <style>{customStyles}</style>
    {data.length === 0 ? (
        <div className="text-center p-5">No hay datos para mostrar</div>
    ) : (
        <>
            <div className="d-flex table-header p-2 ps-3 text-nowrap align-middle">
                <div style={{ width: '15%' }}>Fecha</div>
                <div style={{ width: '25%' }}>Alerta</div>
                <div style={{ width: '12%' }}>Vehículo</div>
                <div style={{ width: '15%' }}>Chofer</div>
                <div style={{ width: '8%', textAlign: 'center' }}>Video</div>
                <div style={{ width: '15%' }}>Geocerca</div>
                <div style={{ width: '10%', textAlign: 'center' }}>Ubicación</div>
            </div>
            
            <div style={{ height: 'calc(65vh - 40px)' }} className="list-container">
                <List
                    className="list-striped"
                    height={window.innerHeight * 0.65 - 40}
                    itemCount={data.length}
                    itemSize={ROW_HEIGHT}
                    width="100%"
                    // Añadir estas propiedades para mejorar el rendimiento
                    overscanCount={5}
                    initialScrollOffset={0}
                >
                    {({ index, style }) => {
                        const item = data[index];
                        return (
                            <MemoizedRow
                                key={item.id || index}
                                item={item}
                                index={index}
                                style={style}
                                handleShowMap={handleShowMap}
                                handleShowVideoModal={handleShowVideoModal}
                                isGroup={isGroup}
                            />
                        );
                    }}
                </List>
            </div>
        </>
    )}
</Card.Body>

                {/* <Card.Body className="p-0">
                    <style>{customStyles}</style>
                    {data.length === 0 ? (
                        <div className="text-center p-5">No hay datos para mostrar</div>
                    ) : (
                        <>
                            <div className="d-flex table-header p-2 ps-3 text-nowrap align-middle">
                                <div style={{ width: '15%' }}>Fecha</div>
                                <div style={{ width: '25%' }}>Alerta</div>
                                <div style={{ width: '12%' }}>Vehículo</div>
                                <div style={{ width: '15%' }}>Chofer</div>
                                <div style={{ width: '8%', textAlign: 'center' }}>Video</div>
                                <div style={{ width: '15%' }}>Geocerca</div>
                                <div style={{ width: '10%', textAlign: 'center' }}>Ubicación</div>
                            </div>
                            <div style={{ height: 'calc(65vh - 40px)', overflow: 'auto' }} className="list-striped">
                                {data.map((item, index) => (
                                    <div
                                        key={`row-${index}`}
                                        className="alert-row d-flex"
                                        style={{
                                            borderBottom: '1px solid #f5f5f5',
                                            alignItems: 'center',
                                            padding: '0.5rem 0.75rem',
                                            minHeight: ROW_HEIGHT
                                        }}
                                    >
                                        <div style={{ width: '15%', fontWeight: '500' }}>{formatFecha(item.fecha)}</div>
                                        <div style={{ width: '25%' }}>
                                            <div className="d-flex align-items-center">
                                                <div className="avatar avatar-xl me-3">
                                                    <div className="avatar-name rounded-circle"
                                                        style={{
                                                            backgroundColor: `${item.color || '#888888'}33`,
                                                            color: item.color || '#888888',
                                                            border: `2px solid ${item.color || '#888888'}`
                                                        }}>
                                                        <span className="fs-9">
                                                            {isGroup ? (item.alerta_real?.[0] || "") : (item.alerta?.[0] || "")}
                                                        </span>
                                                    </div>
                                                </div>
                                                {isGroup ? (
                                                    <div>
                                                        <strong>{item.alerta_real}</strong>
                                                        <br />
                                                        <span>{item.alerta}</span>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <strong>{item.alerta}</strong>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div style={{ width: '12%' }}>{item.no_economico}</div>
                                        <div style={{ width: '15%' }}>{item.chofer || 'N/A'}</div>
                                        <div style={{ width: '8%', textAlign: 'center' }}>
                                            {item.vehiculo_con_camara && item.camara_en_linea ? (
                                                <FaPlayCircle
                                                    size={20}
                                                    className="text-danger cursor-pointer"
                                                    onClick={() => handleShowVideoModal(item)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            ) : (
                                                <span className="text-muted">N/A</span>
                                            )}
                                        </div>
                                        <div style={{ width: '15%' }}>{item.geocerca || 'N/A'}</div>
                                        <div style={{ width: '10%', textAlign: 'center' }}>
                                            <Button size="sm" className="btn-light rounded-5 p-1" onClick={() => handleShowMap(item.latitud, item.longitud)}>
                                                <FaMapMarkerAlt className="text-danger" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </Card.Body> */}
                <Card.Footer>
    <div className="d-flex justify-content-between align-items-center flex-wrap">
        <div className="d-flex align-items-center">
            <span className="me-2">Total: {total} | Página {page} de {totalPages}</span>
            <Form.Control
                type="number"
                min="1"
                max={totalPages}
                value={page}
                onChange={(e) => {
                    const newPage = parseInt(e.target.value);
                    if (newPage >= 1 && newPage <= totalPages) {
                        onPageChange(newPage);
                    }
                }}
                style={{ width: '70px', height: '31px' }}
                size="sm"
            />
        </div>
        <div className="d-flex justify-content-center">
            <Button
                variant="outline-primary"
                size="sm"
                className="mx-1"
                onClick={() => onPageChange(1)}
                disabled={page === 1}
            >
                &laquo; Primera
            </Button>
            <Button
                variant="outline-primary"
                size="sm"
                className="mx-1"
                onClick={() => onPageChange(page - 1)}
                disabled={page === 1}
            >
                &lt; Anterior
            </Button>
            
            {/* Mostrar solo un rango limitado de páginas */}
            {(() => {
                // Determinar qué páginas mostrar
                let pagesArray = [];
                if (totalPages <= 5) {
                    // Si hay 5 o menos páginas, mostrarlas todas
                    pagesArray = Array.from({ length: totalPages }, (_, i) => i + 1);
                } else if (page <= 3) {
                    // Si estamos en las primeras páginas
                    pagesArray = [1, 2, 3, 4, 5];
                } else if (page >= totalPages - 2) {
                    // Si estamos en las últimas páginas
                    pagesArray = [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
                } else {
                    // Estamos en medio, mostrar 2 antes y 2 después
                    pagesArray = [page - 2, page - 1, page, page + 1, page + 2];
                }
                
                return pagesArray.map(pageNum => (
                    <Button
                        key={`page-${pageNum}`}
                        variant={pageNum === page ? "primary" : "outline-primary"}
                        size="sm"
                        className="mx-1"
                        onClick={() => onPageChange(pageNum)}
                    >
                        {pageNum}
                    </Button>
                ));
            })()}
            
            <Button
                variant="outline-primary"
                size="sm"
                className="mx-1"
                onClick={() => onPageChange(page + 1)}
                disabled={page === totalPages}
            >
                Siguiente &gt;
            </Button>
            <Button
                variant="outline-primary"
                size="sm"
                className="mx-1"
                onClick={() => onPageChange(totalPages)}
                disabled={page === totalPages}
            >
                Última &raquo;
            </Button>
        </div>
        <div>
            <Form.Select 
                size="sm" 
                style={{width: '120px'}}
                value={perPage}
                onChange={(e) => onPageSizeChange && onPageSizeChange(parseInt(e.target.value))}
            >
                <option value="50">50 por pág.</option>
                <option value="100">100 por pág.</option>
                <option value="250">250 por pág.</option>
                <option value="500">500 por pág.</option>
            </Form.Select>
        </div>
    </div>
</Card.Footer>
                {/* <Card.Footer>
                    <div className="d-flex justify-content-center flex-wrap">
                        {Array.from({ length: totalPages }, (_, i) => (
                            <Button
                                key={i}
                                variant={i + 1 === page ? "primary" : "outline-primary"}
                                size="sm"
                                className="mx-1 my-1"
                                onClick={() => onPageChange && onPageChange(i + 1)}
                            >
                                {i + 1}
                            </Button>
                        ))}
                    </div>
                </Card.Footer> */}
            </Card>

            {/* Modal de mapa */}
            <Modal show={showMapModal} onHide={() => setShowMapModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Ubicación del Evento</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={12}>
                            {selectedLocation.lat && selectedLocation.lng ? (
                                <Suspense fallback={<div className="text-center p-5"><Spinner animation="border" /></div>}>
                                    <GoogleMapSelector
                                        initialCenter={{
                                            lat: selectedLocation.lat,
                                            lng: selectedLocation.lng,
                                        }}
                                        markers={[{
                                            lat: selectedLocation.lat,
                                            lng: selectedLocation.lng,
                                        }]}
                                        zoomDefault={13}
                                        mapStyle="hoper"
                                        className="vh-50"
                                        showStreetView={false}
                                    />
                                </Suspense>
                            ) : (
                                <p>Ubicación no disponible</p>
                            )}
                        </Col>
                    </Row>
                </Modal.Body>
            </Modal>

            {/* Modal de video */}
            <Modal show={showVideoModal} onHide={limpiarModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <h5>No Económico: {selectedVehicle?.no_economico}</h5>
                        <h6>Fecha: {selectedVehicle ? formatFecha(selectedVehicle.fecha) : ''}</h6>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="mb-3 d-flex justify-content-center">
                        {videoIds.map((id, index) => (
                            <Col xs="auto" key={id} className="text-center">
                                <Form.Check
                                    id={'chk-' + id}
                                    type="checkbox"
                                    label={`Canal ${index + 1}`}
                                    checked={videoChecks[index]}
                                    onChange={(event) => handleToggleVideo(event, index)}
                                    className="fw-bold"
                                />
                            </Col>
                        ))}
                    </Row>
                    <Row>
                        {videoIds.map((id, index) => (
                            <Col md={6} key={id} className="mb-3 d-flex justify-content-center">
                                <div
                                    id={`div-` + id}
                                    className={consultandoCamara[index]
                                        ? `border p-2 text-center w-100 d-flex justify-content-center align-items-center`
                                        : `border p-2 text-center w-100`}
                                    style={{ height: '200px' }}
                                >
                                    {consultandoCamara[index] && <Spinner animation="border" role="status" />}
                                    {videoChecks[index] && (
                                        <Suspense fallback={<Spinner animation="border" />}>
                                            <CamaraVisor
                                                id={`vid-` + id}
                                                vehiculo={selectedVehicle}
                                                checked={videoChecks[index]}
                                                urlVideo={urlVideo[index]}
                                            />
                                        </Suspense>
                                    )}
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Modal.Body>
            </Modal>
        </GoogleMapsProvider>
    );
};

AlertaTableList.propTypes = {
    data: PropTypes.array,
    total: PropTypes.number,
    page: PropTypes.number,
    perPage: PropTypes.number,
    totalPages: PropTypes.number,
    onPageChange: PropTypes.func,
    onPageSizeChange: PropTypes.func,
    onSearch: PropTypes.func,
    searchValue: PropTypes.string,
    title: PropTypes.string,
    isGroup: PropTypes.bool,
    isExporting: PropTypes.bool,
    onExport: PropTypes.func
};

export default React.memo(AlertaTableList);


// import React, { useState, useMemo, useEffect, useCallback, lazy, Suspense } from 'react';
// import PropTypes from 'prop-types';
// import { Card, Row, Col, Modal, Form, Spinner, Button } from 'react-bootstrap';
// import { FaMapMarkerAlt, FaPlayCircle } from 'react-icons/fa';
// import AdvanceTableSearchBox from '../../../../../components/common/advance-table/AdvanceTableSearchBox';
// import AdvanceTableFooter from '../../../../../components/common/advance-table/AdvanceTableFooter';
// import useAdvanceTable from '../../../../../hooks/useAdvanceTable';
// import AdvanceTableProvider from '../../../../../providers/AdvanceTableSearchProvider';
// import dayjs from 'dayjs';
// import customParseFormat from 'dayjs/plugin/customParseFormat';
// import { GoogleMapsProvider } from '../../../../../providers/GoogleMapsProvider';
// import { CamarasService } from '../../../../../services/apiService';
// import Swal from 'sweetalert2';
// import withReactContent from 'sweetalert2-react-content';

// import 'dayjs/locale/es';
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";

// // Carga diferida de componentes pesados
// const GoogleMapComponent = lazy(() => import('../../../../../components/common/GoogleMap'));
// const CamaraVisor = lazy(() => import('../../../../../components/camaras/CamaraVisor'));

// // Activamos el soporte para formatos personalizados
// dayjs.extend(customParseFormat);
// dayjs.locale('es');

// // Constante para altura de fila
// const ROW_HEIGHT = 65;

// // Estilos personalizados para la tabla
// const customStyles = `
// .list-striped .alert-row:nth-child(odd) {
//   background-color: rgba(250, 250, 250, 0.9);
// }
// .list-striped .alert-row:nth-child(even) {
//   background-color: white;
// }
// .list-striped .alert-row:hover {
//   background-color: rgba(245, 245, 245, 1);
// }
// .table-header {
//   background-color: var(--falcon-200);
//   font-weight: 600;
//   font-size: 0.83rem;
//   position: sticky;
//   top: 0;
//   z-index: 1;
// }
// .alert-row {
//   padding: 0.5rem 0.5rem;
//   font-size: 0.83rem;
//   transition: background-color 0.15s ease;
// }
// .avatar-name.rounded-circle {
//   display: flex;
//   align-items: center;
//   justify-content: center;
// }
// `;

// // Función para formatear fechas
// const formatFecha = (fechaStr) => {
//     if (!fechaStr || typeof fechaStr !== 'string') return 'N/A';
//     try {
//         return dayjs(fechaStr, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY hh:mm A');
//     } catch (error) {
//         console.error('Error al formatear la fecha:', error);
//         return 'N/A';
//     }
// };

// // Worker para exportación de datos
// const exportWorker = () => {
//     return new Worker(
//         URL.createObjectURL(
//             new Blob([
//                 `
//                 self.onmessage = function(e) {
//                     const { data, title } = e.data;
//                     // Procesamiento de datos
//                     const formatted = data.map(item => ({
//                         Fecha: item.fecha,
//                         Alerta: item.alerta,
//                         Vehiculo: item.no_economico,
//                         Chofer: item.chofer || 'N/A',
//                         Geocerca: item.geocerca || 'N/A'
//                     }));
//                     self.postMessage({ formatted, title });
//                 }
//                 `
//             ], { type: 'application/javascript' })
//         )
//     );
// };

// // Worker para filtrado/búsqueda (puedes expandir la lógica según tus filtros)
// const filterWorker = () => {
//     return new Worker(
//         URL.createObjectURL(
//             new Blob([
//                 `
//                 self.onmessage = function(e) {
//                     const { data, search } = e.data;
//                     if (!search || search.trim() === "") {
//                         self.postMessage(data);
//                         return;
//                     }
//                     const searchLower = search.toLowerCase();
//                     const filtered = data.filter(item =>
//                         (item.fecha && item.fecha.toLowerCase().includes(searchLower)) ||
//                         (item.alerta && item.alerta.toLowerCase().includes(searchLower)) ||
//                         (item.no_economico && item.no_economico.toLowerCase().includes(searchLower)) ||
//                         (item.chofer && item.chofer.toLowerCase().includes(searchLower)) ||
//                         (item.geocerca && item.geocerca.toLowerCase().includes(searchLower))
//                     );
//                     self.postMessage(filtered);
//                 }
//                 `
//             ], { type: 'application/javascript' })
//         )
//     );
// };

// const AlertaTableList = ({ data = [], perPage = 100, title, isGroup }) => {
//     // Memoización de datos originales
//     const safeTableData = useMemo(() => Array.isArray(data) ? data : [], [data]);

//     // Estados
//     const [filteredData, setFilteredData] = useState(safeTableData);
//     const [searchValue, setSearchValue] = useState('');
//     const [showMapModal, setShowMapModal] = useState(false);
//     const [selectedLocation, setSelectedLocation] = useState({ lat: null, lng: null });
//     const [showVideoModal, setShowVideoModal] = useState(false);
//     const [videoChecks, setVideoChecks] = useState([false, false, false, false]);
//     const [selectedVehicle, setSelectedVehicle] = useState(null);
//     const [urlVideo, setUrlVideo] = useState(["", "", "", ""]);
//     const [consultandoCamara, setConsultandoCamara] = useState([false, false, false, false]);
//     const [isExporting, setIsExporting] = useState(false);
//     const [currentPage, setCurrentPage] = useState(0);
//     const [pageSize, setPageSize] = useState(perPage);
//     const [paginationKey, setPaginationKey] = useState(0);

//     // Memoizar IDs de videos
//     const videoIds = useMemo(() => ["canal-1", "canal-2", "canal-3", "canal-4"], []);
//     const MySwal = withReactContent(Swal);

//     // Filtrado con Web Worker
//     useEffect(() => {
//         const worker = filterWorker();
//         worker.onmessage = (e) => setFilteredData(e.data);
//         worker.postMessage({ data: safeTableData, search: searchValue });
//         return () => worker.terminate();
//     }, [safeTableData, searchValue]);

//     // Handlers
//     const handleShowMap = useCallback((lat, lng) => {
//         setSelectedLocation({ lat, lng });
//         setShowMapModal(true);
//     }, []);

//     const handleShowVideoModal = useCallback((vehicle) => {
//         setSelectedVehicle(vehicle);
//         setShowVideoModal(true);
//     }, []);

//     const handleToggleVideo = useCallback((event, index) => {
//         let _urlVideo = '';
//         if (event.target.checked) {
//             setConsultandoCamara(prev => {
//                 const items = [...prev];
//                 items[index] = true;
//                 return items;
//             });
//             if (selectedVehicle && selectedVehicle.id_camara) {
//                 CamarasService.getHistorialDeVisualizacion({
//                     id_camara: selectedVehicle.id_camara,
//                     canal: index,
//                     fecha_inicio_ticks: selectedVehicle.fecha_ticks,
//                     fecha_fin_ticks: selectedVehicle.fecha_ticks
//                 }).then(resp => {
//                     if (resp.statusCode === 200 && resp.data !== null) {
//                         _urlVideo = resp.data.url;
//                         setUrlVideo(prev => {
//                             const items = [...prev];
//                             items[index] = _urlVideo;
//                             return items;
//                         });
//                     } else {
//                         setVideoChecks(prev => {
//                             const newChecks = [...prev];
//                             newChecks[index] = false;
//                             return newChecks;
//                         });
//                         MySwal.fire({
//                             icon: 'error',
//                             text: resp.data ? 'No se pudo obtener la información' : resp.message,
//                             showConfirmButton: true
//                         });
//                     }
//                     setConsultandoCamara(prev => {
//                         const items = [...prev];
//                         items[index] = false;
//                         return items;
//                     });
//                 });
//             }
//         } else {
//             setUrlVideo(prev => {
//                 const items = [...prev];
//                 items[index] = '';
//                 return items;
//             });
//         }
//         setVideoChecks(prev => {
//             const newChecks = [...prev];
//             newChecks[index] = !newChecks[index];
//             return newChecks;
//         });
//     }, [selectedVehicle, MySwal]);

//     // Exportación con Web Worker
//     const handleExportar = useCallback(() => {
//         setIsExporting(true);
//         const worker = exportWorker();
//         worker.onmessage = (e) => {
//             const { formatted, title } = e.data;
//             import('xlsx').then(XLSX => {
//                 const ws = XLSX.utils.json_to_sheet(formatted);
//                 const wb = XLSX.utils.book_new();
//                 XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: "A1" });
//                 if(!ws['!merges']) ws['!merges'] = [];
//                 ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } });
//                 const colWidths = [];
//                 for (let i = 0; i < 5; i++) colWidths.push({ wch: 15 });
//                 ws['!cols'] = colWidths;
//                 XLSX.utils.book_append_sheet(wb, ws, "Alertas");
//                 XLSX.writeFile(wb, "ReporteDeAlertas.xlsx");
//                 setIsExporting(false);
//             });
//             worker.terminate();
//         };
//         worker.postMessage({ data: filteredData, title });
//     }, [filteredData, title]);

//     const limpiarModal = useCallback(() => {
//         setShowVideoModal(false);
//         setVideoChecks([false, false, false, false]);
//         setUrlVideo(["", "", "", ""]);
//     }, []);

//     // Memoizar las columnas para evitar recreación
//     const columns = useMemo(() => [
//         {
//             accessorKey: 'fecha',
//             header: 'Fecha',
//             cell: ({ row: { original } }) => formatFecha(original.fecha),
//             meta: { headerProps: { className: 'text-900' } }
//         },
//         {
//             accessorKey: 'alerta_real',
//             header: 'Alerta Real',
//             cell: ({ row: { original } }) => original.alerta_real,
//         },
//         {
//             accessorKey: 'alerta',
//             header: 'Alerta',
//             cell: ({ row }) => {
//                 const alertaColor = row.original.color || '#888888';
//                 return (
//                     <div className="d-flex align-items-center">
//                         <div className="avatar avatar-xl me-3">
//                             <div className="avatar-name rounded-circle"
//                                 style={{
//                                     backgroundColor: `${alertaColor}33`,
//                                     color: alertaColor,
//                                     border: `2px solid ${alertaColor}`
//                                 }}>
//                                 <span className="fs-9">
//                                     {isGroup
//                                         ? (row.original.alerta_real?.[0] || "")
//                                         : (row.original.alerta?.[0] || "")}
//                                 </span>
//                             </div>
//                         </div>
//                         {isGroup
//                             ? (<div><strong>{row.original.alerta_real}</strong><br /><span>{row.original.alerta}</span></div>)
//                             : (<div><strong>{row.original.alerta}</strong></div>)}
//                     </div>
//                 );
//             },
//             meta: { headerProps: { className: 'text-900' } }
//         },
//         {
//             accessorKey: 'no_economico',
//             header: 'Vehículo',
//             meta: { headerProps: { className: 'text-900' } }
//         },
//         {
//             accessorKey: 'chofer',
//             header: 'Chofer',
//             meta: { headerProps: { className: 'text-900 text-left' } }
//         },
//         {
//             accessorKey: 'video',
//             header: 'Video',
//             cell: ({ row: { original } }) =>
//                 original.vehiculo_con_camara ? (
//                     original.camara_en_linea ?
//                         <FaPlayCircle
//                             size={24}
//                             className="text-danger cursor-pointer"
//                             onClick={() => handleShowVideoModal(original)}
//                             style={{ cursor: 'pointer' }}
//                         /> : <span className="text-muted">No disponible</span>
//                 ) : (
//                     <span className="text-muted">N/A</span>
//                 ),
//             meta: { headerProps: { className: 'text-900 text-center' }, cellProps: { className: 'text-center' } }
//         },
//         {
//             accessorKey: 'geocerca',
//             header: 'Geocerca',
//             cell: ({ row: { original } }) => original.geocerca || 'N/A',
//             meta: { headerProps: { className: 'text-900' } }
//         },
//         {
//             accessorKey: 'mapa',
//             header: 'Ubicación',
//             cell: ({ row: { original } }) => (
//                 <Button className="btn btn-light btn-sm rounded-5" onClick={() => handleShowMap(original.latitud, original.longitud)}>
//                     <FaMapMarkerAlt className="text-danger" />
//                 </Button>
//             ),
//             meta: { headerProps: { className: 'text-900 text-center' }, cellProps: { className: 'text-center' } }
//         }
//     ], [handleShowMap, handleShowVideoModal, isGroup]);

//     // Configuración de la tabla
//     const table = useAdvanceTable({
//         data: filteredData,
//         columns,
//         sortable: true,
//         pagination: true,
//         perPage,
//         initialState: {
//             sorting: [{ id: 'fecha', desc: false }],
//             columnVisibility: { alerta_real: false }
//         }
//     });

//     // Manejar cambios en la paginación de manera segura
//     useEffect(() => {
//         const currentPageIndex = table?.getState()?.pagination?.pageIndex;
//         const currentPageSize = table?.getState()?.pagination?.pageSize;
//         if (currentPageIndex !== undefined && currentPageSize !== undefined) {
//             if (currentPageIndex !== currentPage || currentPageSize !== pageSize) {
//                 setCurrentPage(currentPageIndex);
//                 setPageSize(currentPageSize);
//                 setPaginationKey(prev => prev + 1);
//             }
//         }
//     }, [
//         table?.getState()?.pagination?.pageIndex,
//         table?.getState()?.pagination?.pageSize,
//         currentPage,
//         pageSize
//     ]);

//     return (
//         <GoogleMapsProvider>
//             <AdvanceTableProvider {...table}>
//                 <Card className="h-100">
//                     <Card.Header>
//                         <Row className="d-flex align-items-center justify-content-between">
//                             <Col md={6}>
//                                 <h6 className="mb-0 text-nowrap py-2 py-xl-2">{title}</h6>
//                             </Col>
//                             <Col md={6} className="d-flex justify-content-end">
//                                 <AdvanceTableSearchBox
//                                     className="input-search-width pr-2 flex-grow-1"
//                                     placeholder="Buscar alertas..."
//                                     value={searchValue}
//                                     onChange={e => setSearchValue(e.target.value)}
//                                 />
//                                 <Button
//                                     variant="outline-secondary"
//                                     size="sm"
//                                     className="me-3 ms-3"
//                                     onClick={handleExportar}
//                                     disabled={isExporting || filteredData.length === 0}
//                                 >
//                                     {isExporting ? (
//                                         <>
//                                             <Spinner
//                                                 as="span"
//                                                 animation="border"
//                                                 size="sm"
//                                                 role="status"
//                                                 aria-hidden="true"
//                                                 className="me-1"
//                                             />
//                                             Exportando...
//                                         </>
//                                     ) : (
//                                         <>
//                                             <FontAwesomeIcon icon={faExternalLinkAlt} /> Exportar
//                                         </>
//                                     )}
//                                 </Button>
//                             </Col>
//                         </Row>
//                     </Card.Header>
//                     <Card.Body className="p-0">
//                         <style>{customStyles}</style>
//                         {filteredData.length === 0 ? (
//                             <div className="text-center p-5">No hay datos para mostrar</div>
//                         ) : (
//                             <>
//                                 {/* Cabecera de la tabla - fija */}
//                                 <div className="d-flex table-header p-2 ps-3 text-nowrap align-middle">
//                                     <div style={{ width: '15%' }}>Fecha</div>
//                                     <div style={{ width: '25%' }}>Alerta</div>
//                                     <div style={{ width: '12%' }}>Vehículo</div>
//                                     <div style={{ width: '15%' }}>Chofer</div>
//                                     <div style={{ width: '8%', textAlign: 'center' }}>Video</div>
//                                     <div style={{ width: '15%' }}>Geocerca</div>
//                                     <div style={{ width: '10%', textAlign: 'center' }}>Ubicación</div>
//                                 </div>
//                                 {/* Contenedor de tabla con altura fija y scroll */}
//                                 <div
//                                     style={{ height: 'calc(65vh - 40px)', overflow: 'auto' }}
//                                     className="list-striped"
//                                     key={`table-container-${paginationKey}`}
//                                 >
//                                     {(() => {
//                                         try {
//                                             const pageIndex = table?.getState()?.pagination?.pageIndex ?? 0;
//                                             const pageSize = table?.getState()?.pagination?.pageSize ?? perPage;
//                                             const startIndex = pageIndex * pageSize;
//                                             const endIndex = Math.min(startIndex + pageSize, filteredData.length);
//                                             return filteredData.slice(startIndex, endIndex).map((item, index) => (
//                                                 <div
//                                                     key={`row-${startIndex + index}-${paginationKey}`}
//                                                     className="alert-row d-flex"
//                                                     style={{
//                                                         borderBottom: '1px solid #f5f5f5',
//                                                         alignItems: 'center',
//                                                         padding: '0.5rem 0.75rem',
//                                                         minHeight: ROW_HEIGHT
//                                                     }}
//                                                 >
//                                                     <div style={{ width: '15%', fontWeight: '500' }}>{formatFecha(item.fecha)}</div>
//                                                     <div style={{ width: '25%' }}>
//                                                         <div className="d-flex align-items-center">
//                                                             {isGroup ? (
//                                                                 <div className="avatar avatar-xl me-3">
//                                                                     <div className="avatar-name rounded-circle"
//                                                                         style={{
//                                                                             backgroundColor: `${item.color || '#888888'}33`,
//                                                                             color: item.color || '#888888',
//                                                                             border: `2px solid ${item.color || '#888888'}`
//                                                                         }}>
//                                                                         <span className="fs-9">
//                                                                             {item.alerta_real?.[0] || ""}
//                                                                         </span>
//                                                                     </div>
//                                                                 </div>
//                                                             ) : (
//                                                                 <div className="avatar avatar-xl me-3">
//                                                                     <div className="avatar-name rounded-circle"
//                                                                         style={{
//                                                                             backgroundColor: `${item.color || '#888888'}33`,
//                                                                             color: item.color || '#888888',
//                                                                             border: `2px solid ${item.color || '#888888'}`
//                                                                         }}>
//                                                                         <span className="fs-9">
//                                                                             {item.alerta?.[0] || ""}
//                                                                         </span>
//                                                                     </div>
//                                                                 </div>
//                                                             )}
//                                                             {isGroup ? (
//                                                                 <div>
//                                                                     <strong>{item.alerta_real}</strong>
//                                                                     <br />
//                                                                     <span>{item.alerta}</span>
//                                                                 </div>
//                                                             ) : (
//                                                                 <div>
//                                                                     <strong>{item.alerta}</strong>
//                                                                 </div>
//                                                             )}
//                                                         </div>
//                                                     </div>
//                                                     <div style={{ width: '12%' }}>{item.no_economico}</div>
//                                                     <div style={{ width: '15%' }}>{item.chofer || 'N/A'}</div>
//                                                     <div style={{ width: '8%', textAlign: 'center' }}>
//                                                         {item.vehiculo_con_camara && item.camara_en_linea ? (
//                                                             <FaPlayCircle
//                                                                 size={20}
//                                                                 className="text-danger cursor-pointer"
//                                                                 onClick={() => handleShowVideoModal(item)}
//                                                                 style={{ cursor: 'pointer' }}
//                                                             />
//                                                         ) : (
//                                                             <span className="text-muted">N/A</span>
//                                                         )}
//                                                     </div>
//                                                     <div style={{ width: '15%' }}>{item.geocerca || 'N/A'}</div>
//                                                     <div style={{ width: '10%', textAlign: 'center' }}>
//                                                         <Button size="sm" className="btn-light rounded-5 p-1" onClick={() => handleShowMap(item.latitud, item.longitud)}>
//                                                             <FaMapMarkerAlt className="text-danger" />
//                                                         </Button>
//                                                     </div>
//                                                 </div>
//                                             ));
//                                         } catch (error) {
//                                             console.error("Error al renderizar la tabla:", error);
//                                             return filteredData.slice(0, perPage).map((item, index) => (
//                                                 <div
//                                                     key={`fallback-row-${index}`}
//                                                     className="alert-row d-flex"
//                                                     style={{
//                                                         borderBottom: '1px solid #f5f5f5',
//                                                         alignItems: 'center',
//                                                         padding: '0.5rem 0.75rem',
//                                                         minHeight: ROW_HEIGHT
//                                                     }}
//                                                 >
//                                                     <div style={{ width: '15%', fontWeight: '500' }}>{formatFecha(item.fecha)}</div>
//                                                     <div style={{ width: '25%' }}>
//                                                         <div className="d-flex align-items-center">
//                                                             {isGroup ? (
//                                                                 <div className="avatar avatar-xl me-3">
//                                                                     <div className="avatar-name rounded-circle"
//                                                                         style={{
//                                                                             backgroundColor: `${item.color || '#888888'}33`,
//                                                                             color: item.color || '#888888',
//                                                                             border: `2px solid ${item.color || '#888888'}`
//                                                                         }}>
//                                                                         <span className="fs-9">
//                                                                             {item.alerta_real?.[0] || ""}
//                                                                         </span>
//                                                                     </div>
//                                                                 </div>
//                                                             ) : (
//                                                                 <div className="avatar avatar-xl me-3">
//                                                                     <div className="avatar-name rounded-circle"
//                                                                         style={{
//                                                                             backgroundColor: `${item.color || '#888888'}33`,
//                                                                             color: item.color || '#888888',
//                                                                             border: `2px solid ${item.color || '#888888'}`
//                                                                         }}>
//                                                                         <span className="fs-9">
//                                                                             {item.alerta?.[0] || ""}
//                                                                         </span>
//                                                                     </div>
//                                                                 </div>
//                                                             )}
//                                                             {isGroup ? (
//                                                                 <div>
//                                                                     <strong>{item.alerta_real}</strong>
//                                                                     <br />
//                                                                     <span>{item.alerta}</span>
//                                                                 </div>
//                                                             ) : (
//                                                                 <div>
//                                                                     <strong>{item.alerta}</strong>
//                                                                 </div>
//                                                             )}
//                                                         </div>
//                                                     </div>
//                                                     <div style={{ width: '12%' }}>{item.no_economico}</div>
//                                                     <div style={{ width: '15%' }}>{item.chofer || 'N/A'}</div>
//                                                     <div style={{ width: '8%', textAlign: 'center' }}>
//                                                         {item.vehiculo_con_camara && item.camara_en_linea ? (
//                                                             <FaPlayCircle
//                                                                 size={20}
//                                                                 className="text-danger cursor-pointer"
//                                                                 onClick={() => handleShowVideoModal(item)}
//                                                                 style={{ cursor: 'pointer' }}
//                                                             />
//                                                         ) : (
//                                                             <span className="text-muted">N/A</span>
//                                                         )}
//                                                     </div>
//                                                     <div style={{ width: '15%' }}>{item.geocerca || 'N/A'}</div>
//                                                     <div style={{ width: '10%', textAlign: 'center' }}>
//                                                         <Button size="sm" className="btn-light rounded-5 p-1" onClick={() => handleShowMap(item.latitud, item.longitud)}>
//                                                             <FaMapMarkerAlt className="text-danger" />
//                                                         </Button>
//                                                     </div>
//                                                 </div>
//                                             ));
//                                         }
//                                     })()}
//                                 </div>
//                             </>
//                         )}
//                     </Card.Body>
//                     <Card.Footer>
//                         <AdvanceTableFooter
//                             rowInfo
//                             navButtons
//                             rowsPerPageSelection
//                             rowsPerPageOptions={[100, 200, 300]}
//                         />
//                     </Card.Footer>
//                 </Card>
//             </AdvanceTableProvider>

//             {/* Modal de mapa - Carga diferida */}
//             <Modal show={showMapModal} onHide={() => setShowMapModal(false)} size="lg" centered>
//                 <Modal.Header closeButton>
//                     <Modal.Title>Ubicación del Evento</Modal.Title>
//                 </Modal.Header>
//                 <Modal.Body>
//                     <Row>
//                         <Col md={12}>
//                             {selectedLocation.lat && selectedLocation.lng ? (
//                                 <Suspense fallback={<div className="text-center p-5"><Spinner animation="border" /></div>}>
//                                     <GoogleMapComponent
//                                         initialCenter={{
//                                             lat: selectedLocation.lat,
//                                             lng: selectedLocation.lng,
//                                         }}
//                                         mapStyle="hoper"
//                                         className="vh-50"
//                                         showStreetView={false}
//                                     />
//                                 </Suspense>
//                             ) : (
//                                 <p>Ubicación no disponible</p>
//                             )}
//                         </Col>
//                     </Row>
//                 </Modal.Body>
//             </Modal>

//             {/* Modal de video - Carga diferida */}
//             <Modal show={showVideoModal} onHide={limpiarModal} size="lg" centered>
//                 <Modal.Header closeButton>
//                     <Modal.Title>
//                         <h5>No Económico: {selectedVehicle?.no_economico}</h5>
//                         <h6>Fecha: {selectedVehicle ? formatFecha(selectedVehicle.fecha) : ''}</h6>
//                     </Modal.Title>
//                 </Modal.Header>
//                 <Modal.Body>
//                     <Row className="mb-3 d-flex justify-content-center">
//                         {videoIds.map((id, index) => (
//                             <Col xs="auto" key={id} className="text-center">
//                                 <Form.Check
//                                     id={'chk-' + id}
//                                     type="checkbox"
//                                     label={`Canal ${index + 1}`}
//                                     checked={videoChecks[index]}
//                                     onChange={(event) => handleToggleVideo(event, index)}
//                                     className="fw-bold"
//                                 />
//                             </Col>
//                         ))}
//                     </Row>
//                     <Row>
//                         {videoIds.map((id, index) => (
//                             <Col md={6} key={id} className="mb-3 d-flex justify-content-center">
//                                 <div
//                                     id={`div-` + id}
//                                     className={consultandoCamara[index]
//                                         ? `border p-2 text-center w-100 d-flex justify-content-center align-items-center`
//                                         : `border p-2 text-center w-100`}
//                                     style={{ height: '200px' }}
//                                 >
//                                     {consultandoCamara[index] && <Spinner animation="border" role="status" />}
//                                     {videoChecks[index] && (
//                                         <Suspense fallback={<Spinner animation="border" />}>
//                                             <CamaraVisor
//                                                 id={`vid-` + id}
//                                                 vehiculo={selectedVehicle}
//                                                 checked={videoChecks[index]}
//                                                 urlVideo={urlVideo[index]}
//                                             />
//                                         </Suspense>
//                                     )}
//                                 </div>
//                             </Col>
//                         ))}
//                     </Row>
//                 </Modal.Body>
//             </Modal>
//         </GoogleMapsProvider>
//     );
// };

// AlertaTableList.propTypes = {
//     data: PropTypes.array,
//     perPage: PropTypes.number,
//     title: PropTypes.string,
//     isGroup: PropTypes.bool
// };

// export default React.memo(AlertaTableList);




// import React, { useState, useMemo, useEffect, useCallback, lazy, Suspense } from 'react';
// import PropTypes from 'prop-types';
// import { Card, Row, Col, Modal, Form, Spinner, Button } from 'react-bootstrap';
// import { FaMapMarkerAlt, FaPlayCircle } from 'react-icons/fa';
// import AdvanceTableSearchBox from '../../../../../components/common/advance-table/AdvanceTableSearchBox';
// import AdvanceTableFooter from '../../../../../components/common/advance-table/AdvanceTableFooter';
// import useAdvanceTable from '../../../../../hooks/useAdvanceTable';
// import AdvanceTableProvider from '../../../../../providers/AdvanceTableSearchProvider';
// import dayjs from 'dayjs';
// import customParseFormat from 'dayjs/plugin/customParseFormat';
// import { GoogleMapsProvider } from '../../../../../providers/GoogleMapsProvider';
// import { CamarasService } from '../../../../../services/apiService';
// import Swal from 'sweetalert2';
// import withReactContent from 'sweetalert2-react-content';

// import 'dayjs/locale/es';
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";

// // Carga diferida de componentes pesados
// const GoogleMapComponent = lazy(() => import('../../../../../components/common/GoogleMap'));
// const CamaraVisor = lazy(() => import('../../../../../components/camaras/CamaraVisor'));

// // Activamos el soporte para formatos personalizados
// dayjs.extend(customParseFormat);
// dayjs.locale('es');

// // Constante para altura de fila
// const ROW_HEIGHT = 65;

// // Estilos personalizados para la tabla
// const customStyles = `
// .list-striped .alert-row:nth-child(odd) {
//   background-color: rgba(250, 250, 250, 0.9);
// }

// .list-striped .alert-row:nth-child(even) {
//   background-color: white;
// }

// .list-striped .alert-row:hover {
//   background-color: rgba(245, 245, 245, 1);
// }

// .table-header {
//   background-color: var(--falcon-200);
//   font-weight: 600;
//   font-size: 0.83rem;
//   position: sticky;
//   top: 0;
//   z-index: 1;
// }

// .alert-row {
//   padding: 0.5rem 0.5rem;
//   font-size: 0.83rem;
//   transition: background-color 0.15s ease;
// }

// .avatar-name.rounded-circle {
//   display: flex;
//   align-items: center;
//   justify-content: center;
// }
// `;

// // Función para formatear fechas
// const formatFecha = (fechaStr) => {
//     if (!fechaStr || typeof fechaStr !== 'string') return 'N/A';

//     try {
//         return dayjs(fechaStr, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY hh:mm A');
//     } catch (error) {
//         console.error('Error al formatear la fecha:', error);
//         return 'N/A';
//     }
// };

// // Worker para exportación de datos
// const exportWorker = () => {
//     return new Worker(
//         URL.createObjectURL(
//             new Blob([
//                 `
//                 self.onmessage = function(e) {
//                     const { data, title } = e.data;
                    
//                     // Procesamiento de datos
//                     const formatted = data.map(item => ({
//                         Fecha: item.fecha,
//                         Alerta: item.alerta,
//                         Vehiculo: item.no_economico,
//                         Chofer: item.chofer || 'N/A',
//                         Geocerca: item.geocerca || 'N/A'
//                     }));
                    
//                     self.postMessage({ formatted, title });
//                 }
//                 `
//             ], { type: 'application/javascript' })
//         )
//     );
// };

// const AlertaTableList = ({ data = [], perPage = 100, title, isGroup }) => {
//     // Verificación y memoización de los datos
//     const safeTableData = useMemo(() => {
//         return Array.isArray(data) ? data : [];
//     }, [data]);
    
//     // Estados con valores iniciales correctos
//     const [showMapModal, setShowMapModal] = useState(false);
//     const [selectedLocation, setSelectedLocation] = useState({ lat: null, lng: null });
//     const [showVideoModal, setShowVideoModal] = useState(false);
//     const [videoChecks, setVideoChecks] = useState([false, false, false, false]);
//     const [selectedVehicle, setSelectedVehicle] = useState(null);
//     const [urlVideo, setUrlVideo] = useState(["", "", "", ""]);
//     const [consultandoCamara, setConsultandoCamara] = useState([false, false, false, false]);
//     const [isExporting, setIsExporting] = useState(false);
    
//     // Estados para controlar la paginación de forma segura
//     const [currentPage, setCurrentPage] = useState(0);
//     const [pageSize, setPageSize] = useState(perPage);
//     const [paginationKey, setPaginationKey] = useState(0); // Forzar re-render cuando cambia la página
    
//     // Memoizar IDs de videos para evitar recreación
//     const videoIds = useMemo(() => ["canal-1", "canal-2", "canal-3", "canal-4"], []);
    
//     const MySwal = withReactContent(Swal);

//     // Convertir handlers a useCallback para evitar recreaciones
//     const handleShowMap = useCallback((lat, lng) => {
//         setSelectedLocation({ lat, lng });
//         setShowMapModal(true);
//     }, []);

//     const handleShowVideoModal = useCallback((vehicle) => {
//         setSelectedVehicle(vehicle);
//         setShowVideoModal(true);
//     }, []);

//     const handleToggleVideo = useCallback((event, index) => {
//         let _urlVideo = '';

//         if (event.target.checked) {
//             setConsultandoCamara(prev => {
//                 const items = [...prev];
//                 items[index] = true;
//                 return items;
//             });

//             // Solo realizar la petición si hay un vehículo seleccionado
//             if (selectedVehicle && selectedVehicle.id_camara) {
//                 CamarasService.getHistorialDeVisualizacion({
//                     id_camara: selectedVehicle.id_camara,
//                     canal: index,
//                     fecha_inicio_ticks: selectedVehicle.fecha_ticks,
//                     fecha_fin_ticks: selectedVehicle.fecha_ticks
//                 }).then(resp => {
//                     if (resp.statusCode === 200 && resp.data !== null) {
//                         _urlVideo = resp.data.url;
//                         setUrlVideo(prev => {
//                             const items = [...prev];
//                             items[index] = _urlVideo;
//                             return items;
//                         });
//                     } else {
//                         setVideoChecks(prev => {
//                             const newChecks = [...prev];
//                             newChecks[index] = false;
//                             return newChecks;
//                         });
                        
//                         MySwal.fire({
//                             icon: 'error',
//                             text: resp.data ? 'No se pudo obtener la información' : resp.message,
//                             showConfirmButton: true
//                         });
//                     }
                    
//                     setConsultandoCamara(prev => {
//                         const items = [...prev];
//                         items[index] = false;
//                         return items;
//                     });
//                 });
//             }
//         } else {
//             setUrlVideo(prev => {
//                 const items = [...prev];
//                 items[index] = '';
//                 return items;
//             });
//         }

//         setVideoChecks(prev => {
//             const newChecks = [...prev];
//             newChecks[index] = !newChecks[index];
//             return newChecks;
//         });
//     }, [selectedVehicle, MySwal]);

//     // Exportación con Web Worker
//     const handleExportar = useCallback(() => {
//         setIsExporting(true);
        
//         // Crear worker para procesamiento en segundo plano
//         const worker = exportWorker();
        
//         worker.onmessage = (e) => {
//             const { formatted, title } = e.data;
            
//             // Usar XLSX en el hilo principal solo para la creación final
//             import('xlsx').then(XLSX => {
//                 const ws = XLSX.utils.json_to_sheet(formatted);
//                 const wb = XLSX.utils.book_new();
                
//                 // Agregar título
//                 XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: "A1" });
                
//                 // Combinar celdas para el título
//                 if(!ws['!merges']) ws['!merges'] = [];
//                 ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } });
                
//                 // Ajustar anchos de columna automáticamente
//                 const colWidths = [];
//                 for (let i = 0; i < 5; i++) {
//                     colWidths.push({ wch: 15 });
//                 }
//                 ws['!cols'] = colWidths;
                
//                 XLSX.utils.book_append_sheet(wb, ws, "Alertas");
//                 XLSX.writeFile(wb, "ReporteDeAlertas.xlsx");
                
//                 setIsExporting(false);
//             });
            
//             worker.terminate();
//         };
        
//         worker.postMessage({ data: safeTableData, title });
//     }, [safeTableData, title]);

//     const limpiarModal = useCallback(() => {
//         setShowVideoModal(false);
//         setVideoChecks([false, false, false, false]);
//         setUrlVideo(["", "", "", ""]);
//     }, []);

//     // Memoizar las columnas para evitar recreación
//     const columns = useMemo(() => [
//         {
//             accessorKey: 'fecha',
//             header: 'Fecha',
//             cell: ({ row: { original } }) => formatFecha(original.fecha),
//             meta: { headerProps: { className: 'text-900' } }
//         },
//         {
//             accessorKey: 'alerta_real',
//             header: 'Alerta Real',
//             cell: ({ row: { original } }) => original.alerta_real,
//         },
//         {
//             accessorKey: 'alerta',
//             header: 'Alerta',
//             cell: ({ row }) => {
//                 const alertaColor = row.original.color || '#888888';
//                 return (
//                     <div className="d-flex align-items-center">
//                         <div className="avatar avatar-xl me-3">
//                             <div className="avatar-name rounded-circle"
//                                 style={{
//                                     backgroundColor: `${alertaColor}33`,
//                                     color: alertaColor,
//                                     border: `2px solid ${alertaColor}`
//                                 }}>
//                                 <span className="fs-9">
//                                     {isGroup 
//                                         ? (row.original.alerta_real?.[0] || "") 
//                                         : (row.original.alerta?.[0] || "")}
//                                 </span>
//                             </div>
//                         </div>
//                         {isGroup 
//                             ? (<div><strong>{row.original.alerta_real}</strong><br/><span>{row.original.alerta}</span></div>) 
//                             : (<div><strong>{row.original.alerta}</strong></div>)}
//                     </div>
//                 );
//             },
//             meta: { headerProps: { className: 'text-900' } }
//         },
//         {
//             accessorKey: 'no_economico',
//             header: 'Vehículo',
//             meta: { headerProps: { className: 'text-900' } }
//         },
//         {
//             accessorKey: 'chofer',
//             header: 'Chofer',
//             meta: { headerProps: { className: 'text-900 text-left' } }
//         },
//         {
//             accessorKey: 'video',
//             header: 'Video',
//             cell: ({ row: { original } }) =>
//                 original.vehiculo_con_camara ? (
//                     original.camara_en_linea ?
//                         <FaPlayCircle
//                             size={24}
//                             className="text-danger cursor-pointer"
//                             onClick={() => handleShowVideoModal(original)}
//                             style={{ cursor: 'pointer' }}
//                         /> : <span className="text-muted">No disponible</span>
//                 ) : (
//                     <span className="text-muted">N/A</span>
//                 ),
//             meta: { headerProps: { className: 'text-900 text-center' }, cellProps: { className: 'text-center' } }
//         },
//         {
//             accessorKey: 'geocerca',
//             header: 'Geocerca',
//             cell: ({ row: { original } }) => original.geocerca || 'N/A',
//             meta: { headerProps: { className: 'text-900' } }
//         },
//         {
//             accessorKey: 'mapa',
//             header: 'Ubicación',
//             cell: ({ row: { original } }) => (
//                 <Button className="btn btn-light btn-sm rounded-5" onClick={() => handleShowMap(original.latitud, original.longitud)}>
//                     <FaMapMarkerAlt className="text-danger" />
//                 </Button>
//             ),
//             meta: { headerProps: { className: 'text-900 text-center' }, cellProps: { className: 'text-center' } }
//         }
//     ], [handleShowMap, handleShowVideoModal, isGroup]);

//     // Configuración de la tabla
//     const table = useAdvanceTable({
//         data: safeTableData,
//         columns,
//         sortable: true,
//         pagination: true,
//         perPage,
//         initialState: {
//             sorting: [{ id: 'fecha', desc: false }],
//             columnVisibility: { alerta_real: false }
//         }
//     });

//     // Manejar cambios en la paginación de manera segura
//     useEffect(() => {
//     // Get the current pagination state directly from the table
//     const currentPageIndex = table?.getState()?.pagination?.pageIndex;
//     const currentPageSize = table?.getState()?.pagination?.pageSize;
    
//     // Only update if we have valid pagination values
//     if (currentPageIndex !== undefined && currentPageSize !== undefined) {
//         // Check if pagination actually changed
//         if (currentPageIndex !== currentPage || currentPageSize !== pageSize) {
//             setCurrentPage(currentPageIndex);
//             setPageSize(currentPageSize);
//             // Force a re-render by incrementing the key
//             setPaginationKey(prev => prev + 1);
            
//             console.log(`Paginación actualizada: página ${currentPageIndex + 1}, tamaño ${currentPageSize}`);
//         }
//     }
// }, [
//     // These specific dependencies will ensure the effect runs when pagination changes
//     table?.getState()?.pagination?.pageIndex,
//     table?.getState()?.pagination?.pageSize,
//     currentPage,
//     pageSize
// ]);
//     // useEffect(() => {
//     //     // Verificación segura de que table y table.state existen
//     //     if (table && table.state && table.state.pagination) {
//     //         const newPageIndex = table.state.pagination.pageIndex || 0;
//     //         const newPageSize = table.state.pagination.pageSize || perPage;
            
//     //         // Actualizar el estado local solo si realmente cambió
//     //         if (newPageIndex !== currentPage || newPageSize !== pageSize) {
//     //             setCurrentPage(newPageIndex);
//     //             setPageSize(newPageSize);
//     //             // Incrementar la clave para forzar una re-renderización
//     //             setPaginationKey(prevKey => prevKey + 1);
                
//     //             console.log(`Paginación actualizada: página ${newPageIndex + 1}, tamaño ${newPageSize}`);
//     //         }
//     //     }
//     // }, [table?.state?.pagination, perPage, currentPage, pageSize]);

//     return (
//         <GoogleMapsProvider>
//             <AdvanceTableProvider {...table}>
//                 <Card className="h-100">
//                     <Card.Header>
//                         <Row className="d-flex align-items-center justify-content-between">
//                             <Col md={6}>
//                                 <h6 className="mb-0 text-nowrap py-2 py-xl-2">{title}</h6>
//                             </Col>
//                             <Col md={6} className="d-flex justify-content-end">
//                                 <AdvanceTableSearchBox
//                                     className="input-search-width pr-2 flex-grow-1"
//                                     placeholder="Buscar alertas..."
//                                 />
//                                 <Button
//                                     variant="outline-secondary"
//                                     size="sm"
//                                     className="me-3 ms-3"
//                                     onClick={handleExportar}
//                                     disabled={isExporting || safeTableData.length === 0}
//                                 >
//                                     {isExporting ? (
//                                         <>
//                                             <Spinner
//                                                 as="span"
//                                                 animation="border"
//                                                 size="sm"
//                                                 role="status"
//                                                 aria-hidden="true"
//                                                 className="me-1"
//                                             />
//                                             Exportando...
//                                         </>
//                                     ) : (
//                                         <>
//                                             <FontAwesomeIcon icon={faExternalLinkAlt} /> Exportar
//                                         </>
//                                     )}
//                                 </Button>
//                             </Col>
//                         </Row>
//                     </Card.Header>

//                     <Card.Body className="p-0">
//                         <style>{customStyles}</style>
//                         {safeTableData.length === 0 ? (
//                             <div className="text-center p-5">No hay datos para mostrar</div>
//                         ) : (
//                             <>
//                                 {/* Cabecera de la tabla - fija */}
//                                 <div className="d-flex table-header p-2 ps-3 text-nowrap align-middle">
//                                     <div style={{ width: '15%' }}>Fecha</div>
//                                     <div style={{ width: '25%' }}>Alerta</div>
//                                     <div style={{ width: '12%' }}>Vehículo</div>
//                                     <div style={{ width: '15%' }}>Chofer</div>
//                                     <div style={{ width: '8%', textAlign: 'center' }}>Video</div>
//                                     <div style={{ width: '15%' }}>Geocerca</div>
//                                     <div style={{ width: '10%', textAlign: 'center' }}>Ubicación</div>
//                                 </div>
                                
//                                 {/* Contenedor de tabla con altura fija y scroll */}
//                                 <div 
//                                     style={{ height: 'calc(65vh - 40px)', overflow: 'auto' }} 
//                                     className="list-striped"
//                                     key={`table-container-${paginationKey}`} // Forzar re-render cuando cambia la página
//                                 >
//                                     {(() => {
//                                         try {
//                                             // Verificación segura de paginación
//                                             const pageIndex = table?.getState()?.pagination?.pageIndex ?? 0;
//                                             const pageSize = table?.getState()?.pagination?.pageSize ?? perPage;
//                                             const startIndex = pageIndex * pageSize;
//                                             const endIndex = Math.min(startIndex + pageSize, safeTableData.length);
                                            
//                                             console.log(`Renderizando página ${pageIndex + 1}: elementos ${startIndex + 1}-${endIndex} de ${safeTableData.length}`);
                                            
//                                             // Extraer solo los elementos de la página actual
//                                             return safeTableData.slice(startIndex, endIndex).map((item, index) => (

//                                                 <div 
//                                                     key={`row-${startIndex + index}-${paginationKey}`}
//                                                     className="alert-row d-flex"
//                                                     style={{
//                                                         borderBottom: '1px solid #f5f5f5',
//                                                         alignItems: 'center',
//                                                         padding: '0.5rem 0.75rem',
//                                                         minHeight: ROW_HEIGHT
//                                                     }}
//                                                 >
//                                                     <div style={{ width: '15%', fontWeight: '500' }}>{formatFecha(item.fecha)}</div>
//                                                     <div style={{ width: '25%' }}>
//                                                         <div className="d-flex align-items-center">
//                                                             {isGroup ? (
//                                                                 <div className="avatar avatar-xl me-3">
//                                                                     <div className="avatar-name rounded-circle"
//                                                                         style={{
//                                                                             backgroundColor: `${item.color || '#888888'}33`,
//                                                                             color: item.color || '#888888',
//                                                                             border: `2px solid ${item.color || '#888888'}`
//                                                                         }}>
//                                                                         <span className="fs-9">
//                                                                             {item.alerta_real?.[0] || ""}
//                                                                         </span>
//                                                                     </div>
//                                                                 </div>
//                                                             ) : (
//                                                                 <div className="avatar avatar-xl me-3">
//                                                                     <div className="avatar-name rounded-circle"
//                                                                         style={{
//                                                                             backgroundColor: `${item.color || '#888888'}33`,
//                                                                             color: item.color || '#888888',
//                                                                             border: `2px solid ${item.color || '#888888'}`
//                                                                         }}>
//                                                                         <span className="fs-9">
//                                                                             {item.alerta?.[0] || ""}
//                                                                         </span>
//                                                                     </div>
//                                                                 </div>
//                                                             )}
//                                                             {isGroup ? (
//                                                                 <div>
//                                                                     <strong>{item.alerta_real}</strong>
//                                                                     <br />
//                                                                     <span>{item.alerta}</span>
//                                                                 </div>
//                                                             ) : (
//                                                                 <div>
//                                                                     <strong>{item.alerta}</strong>
//                                                                 </div>
//                                                             )}
//                                                         </div>
//                                                     </div>
//                                                     <div style={{ width: '12%' }}>{item.no_economico}</div>
//                                                     <div style={{ width: '15%' }}>{item.chofer || 'N/A'}</div>
//                                                     <div style={{ width: '8%', textAlign: 'center' }}>
//                                                         {item.vehiculo_con_camara && item.camara_en_linea ? (
//                                                             <FaPlayCircle
//                                                                 size={20}
//                                                                 className="text-danger cursor-pointer"
//                                                                 onClick={() => handleShowVideoModal(item)}
//                                                                 style={{ cursor: 'pointer' }}
//                                                             />
//                                                         ) : (
//                                                             <span className="text-muted">N/A</span>
//                                                         )}
//                                                     </div>
//                                                     <div style={{ width: '15%' }}>{item.geocerca || 'N/A'}</div>
//                                                     <div style={{ width: '10%', textAlign: 'center' }}>
//                                                         <Button size="sm" className="btn-light rounded-5 p-1" onClick={() => handleShowMap(item.latitud, item.longitud)}>
//                                                             <FaMapMarkerAlt className="text-danger" />
//                                                         </Button>
//                                                     </div>
//                                                 </div>
//                                             ));
//                                         } catch (error) {
//                                             console.error("Error al renderizar la tabla:", error);
//                                             // Fallback en caso de error - mostrar primera página
//                                             return safeTableData.slice(0, perPage).map((item, index) => (
//                                                 <div 
//                                                     key={`fallback-row-${index}`}
//                                                     className="alert-row d-flex"
//                                                     style={{
//                                                         borderBottom: '1px solid #f5f5f5',
//                                                         alignItems: 'center',
//                                                         padding: '0.5rem 0.75rem',
//                                                         minHeight: ROW_HEIGHT
//                                                     }}
//                                                 >
//                                                     {/* Contenido de fallback similar al contenido normal */}
//                                                     <div style={{ width: '15%', fontWeight: '500' }}>{formatFecha(item.fecha)}</div>
//                                                     <div style={{ width: '25%' }}>
//                                                         <div className="d-flex align-items-center">
//                                                             {isGroup ? (
//                                                                 <div className="avatar avatar-xl me-3">
//                                                                     <div className="avatar-name rounded-circle"
//                                                                         style={{
//                                                                             backgroundColor: `${item.color || '#888888'}33`,
//                                                                             color: item.color || '#888888',
//                                                                             border: `2px solid ${item.color || '#888888'}`
//                                                                         }}>
//                                                                         <span className="fs-9">
//                                                                             {item.alerta_real?.[0] || ""}
//                                                                         </span>
//                                                                     </div>
//                                                                 </div>
//                                                             ) : (
//                                                                 <div className="avatar avatar-xl me-3">
//                                                                     <div className="avatar-name rounded-circle"
//                                                                         style={{
//                                                                             backgroundColor: `${item.color || '#888888'}33`,
//                                                                             color: item.color || '#888888',
//                                                                             border: `2px solid ${item.color || '#888888'}`
//                                                                         }}>
//                                                                         <span className="fs-9">
//                                                                             {item.alerta?.[0] || ""}
//                                                                         </span>
//                                                                     </div>
//                                                                 </div>
//                                                             )}
//                                                             {isGroup ? (
//                                                                 <div>
//                                                                     <strong>{item.alerta_real}</strong>
//                                                                     <br />
//                                                                     <span>{item.alerta}</span>
//                                                                 </div>
//                                                             ) : (
//                                                                 <div>
//                                                                     <strong>{item.alerta}</strong>
//                                                                 </div>
//                                                             )}
//                                                         </div>
//                                                     </div>
//                                                     <div style={{ width: '12%' }}>{item.no_economico}</div>
//                                                     <div style={{ width: '15%' }}>{item.chofer || 'N/A'}</div>
//                                                     <div style={{ width: '8%', textAlign: 'center' }}>
//                                                         {item.vehiculo_con_camara && item.camara_en_linea ? (
//                                                             <FaPlayCircle
//                                                                 size={20}
//                                                                 className="text-danger cursor-pointer"
//                                                                 onClick={() => handleShowVideoModal(item)}
//                                                                 style={{ cursor: 'pointer' }}
//                                                             />
//                                                         ) : (
//                                                             <span className="text-muted">N/A</span>
//                                                         )}
//                                                     </div>
//                                                     <div style={{ width: '15%' }}>{item.geocerca || 'N/A'}</div>
//                                                     <div style={{ width: '10%', textAlign: 'center' }}>
//                                                         <Button size="sm" className="btn-light rounded-5 p-1" onClick={() => handleShowMap(item.latitud, item.longitud)}>
//                                                             <FaMapMarkerAlt className="text-danger" />
//                                                         </Button>
//                                                     </div>
//                                                 </div>
//                                             ));
//                                         }
//                                     })()}
//                                 </div>
//                             </>
//                         )}
//                     </Card.Body>
//                     <Card.Footer>
//                         <AdvanceTableFooter 
//                             rowInfo 
//                             navButtons 
//                             rowsPerPageSelection 
//                             rowsPerPageOptions={[100, 200, 300]} 
//                         />
//                     </Card.Footer>
//                 </Card>
//             </AdvanceTableProvider>

//             {/* Modal de mapa - Carga diferida */}
//             <Modal show={showMapModal} onHide={() => setShowMapModal(false)} size="lg" centered>
//                 <Modal.Header closeButton>
//                     <Modal.Title>Ubicación del Evento</Modal.Title>
//                 </Modal.Header>
//                 <Modal.Body>
//                     <Row>
//                         <Col md={12}>
//                             {selectedLocation.lat && selectedLocation.lng ? (
//                                 <Suspense fallback={<div className="text-center p-5"><Spinner animation="border" /></div>}>
//                                     <GoogleMapComponent
//                                         initialCenter={{
//                                             lat: selectedLocation.lat,
//                                             lng: selectedLocation.lng,
//                                         }}
//                                         mapStyle="hoper"
//                                         className="vh-50"
//                                         showStreetView={false}
//                                     />
//                                 </Suspense>
//                             ) : (
//                                 <p>Ubicación no disponible</p>
//                             )}
//                         </Col>
//                     </Row>
//                 </Modal.Body>
//             </Modal>

//             {/* Modal de video - Carga diferida */}
//             <Modal show={showVideoModal} onHide={limpiarModal} size="lg" centered>
//                 <Modal.Header closeButton>
//                     <Modal.Title>
//                         <h5>No Económico: {selectedVehicle?.no_economico}</h5>
//                         <h6>Fecha: {selectedVehicle ? formatFecha(selectedVehicle.fecha) : ''}</h6>
//                     </Modal.Title>
//                 </Modal.Header>
//                 <Modal.Body>
//                     <Row className="mb-3 d-flex justify-content-center">
//                         {videoIds.map((id, index) => (
//                             <Col xs="auto" key={id} className="text-center">
//                                 <Form.Check
//                                     id={'chk-' + id}
//                                     type="checkbox"
//                                     label={`Canal ${index + 1}`}
//                                     checked={videoChecks[index]}
//                                     onChange={(event) => handleToggleVideo(event, index)}
//                                     className="fw-bold"
//                                 />
//                             </Col>
//                         ))}
//                     </Row>
//                     <Row>
//                         {videoIds.map((id, index) => (
//                             <Col md={6} key={id} className="mb-3 d-flex justify-content-center">
//                                 <div
//                                     id={`div-` + id}
//                                     className={consultandoCamara[index] 
//                                         ? `border p-2 text-center w-100 d-flex justify-content-center align-items-center` 
//                                         : `border p-2 text-center w-100`}
//                                     style={{ height: '200px' }}
//                                 >
//                                     {consultandoCamara[index] && <Spinner animation="border" role="status" />}
//                                     {videoChecks[index] && (
//                                         <Suspense fallback={<Spinner animation="border" />}>
//                                             <CamaraVisor
//                                                 id={`vid-` + id}
//                                                 vehiculo={selectedVehicle}
//                                                 checked={videoChecks[index]}
//                                                 urlVideo={urlVideo[index]}
//                                             />
//                                         </Suspense>
//                                     )}
//                                 </div>
//                             </Col>
//                         ))}
//                     </Row>
//                 </Modal.Body>
//             </Modal>
//         </GoogleMapsProvider>
//     );
// };

// AlertaTableList.propTypes = {
//     data: PropTypes.array,
//     perPage: PropTypes.number,
//     title: PropTypes.string,
//     isGroup: PropTypes.bool
// };

// export default React.memo(AlertaTableList);



// import React, { useState } from 'react';
// import PropTypes from 'prop-types';
// import { Card, Row, Col, Modal, Form, Spinner, Button } from 'react-bootstrap';
// import { FaMapMarkerAlt, FaPlayCircle } from 'react-icons/fa';
// import { Link } from 'react-router-dom';
// import AdvanceTable from '../../../../../components/common/advance-table/AdvanceTable';
// import AdvanceTableSearchBox from '../../../../../components/common/advance-table/AdvanceTableSearchBox';
// import AdvanceTableFooter from '../../../../../components/common/advance-table/AdvanceTableFooter';
// import useAdvanceTable from '../../../../../hooks/useAdvanceTable';
// import AdvanceTableProvider from '../../../../../providers/AdvanceTableSearchProvider';
// import dayjs from 'dayjs';
// import customParseFormat from 'dayjs/plugin/customParseFormat';
// import GoogleMapComponent from '../../../../../components/common/GoogleMap';
// import { GoogleMapsProvider } from '../../../../../providers/GoogleMapsProvider';
// import CamaraVisor from '../../../../../components/camaras/CamaraVisor';
// import { CamarasService } from '../../../../../services/apiService';
// import Swal from 'sweetalert2';
// import withReactContent from 'sweetalert2-react-content';

// import 'dayjs/locale/es';
// import { ColumnVisibility } from '@tanstack/react-table';
// import AdvanceTableCustom from '../../../../../components/common/advance-table/AdvanceTableCustom';

// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
// import * as XLSX from 'xlsx'

// // Activamos el soporte para formatos personalizados
// dayjs.extend(customParseFormat);
// dayjs.locale('es');

// const formatFecha = (fechaStr) => {
//     if (!fechaStr || typeof fechaStr !== 'string') return 'N/A';

//     try {
//         return dayjs(fechaStr, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY hh:mm A');
//     } catch (error) {
//         console.error('Error al formatear la fecha:', error);
//         return 'N/A';
//     }
// };

// const AlertaTableList = ({ data = [], perPage = 100, title, isGroup }) => {  // 🔹 Se asegura un valor predeterminado para evitar errores

//     const safeTableData = Array.isArray(data) ? data : [];

//     console.log("Data: ", safeTableData)

//     // Estado para manejar el modal del mapa
//     const [showMapModal, setShowMapModal] = useState(false);
//     const [selectedLocation, setSelectedLocation] = useState({ lat: null, lng: null });

//     // Estado del modal de video
//     const [showVideoModal, setShowVideoModal] = useState(false);
//     const [videoChecks, setVideoChecks] = useState([false, false, false, false]); // Inicialmente los primeros dos videos están activos
//     const [selectedVehicle, setSelectedVehicle] = useState(null);
//     const [urlVideo, setUrlVideo] = useState(["", "", "", ""])
//     const [consultandoCamara, setConsultandoCamara] = useState([false, false, false, false])

//     // IDs de los videos
//     const videoIds = ["canal-1", "canal-2", "canal-3", "canal-4"];

//     // Función para abrir el modal con la latitud y longitud seleccionadas
//     const handleShowMap = (lat, lng) => {
//         setSelectedLocation({ lat, lng });
//         setShowMapModal(true);
//     };

//     // Abrir modal y setear vehículo
//     const handleShowVideoModal = (vehicle) => {
//         setSelectedVehicle(vehicle);
//         setShowVideoModal(true);
//     };

//     const MySwal = withReactContent(Swal)

//     // Manejar visibilidad de los videos
//     const handleToggleVideo = (event, index) => {

//         let _urlVideo = ''

//         if (event.target.checked) {

//             setConsultandoCamara((prev) => {
//                 const items = [...prev]
//                 items[index] = true
//                 return items
//             })

//             const _data = CamarasService.getHistorialDeVisualizacion({
//                 id_camara: selectedVehicle.id_camara,
//                 canal: index,
//                 fecha_inicio_ticks: selectedVehicle.fecha_ticks,
//                 fecha_fin_ticks: selectedVehicle.fecha_ticks
//             });

//             _data.then(resp => {

//                 if (resp.statusCode === 200) {

//                     if (resp.data !== null) {

//                         _urlVideo = resp.data.url;
//                     } else {

//                         setVideoChecks((prev) => {
//                             const newChecks = [...prev];
//                             newChecks[index] = false;
//                             return newChecks;
//                         });

//                         MySwal.fire({
//                             icon: 'error',
//                             text: 'No se pudo obtener la infomación',
//                             showConfirmButton: true
//                         })
//                     }
//                 } else {

//                     setVideoChecks((prev) => {
//                         const newChecks = [...prev];
//                         newChecks[index] = false;
//                         return newChecks;
//                     });

//                     MySwal.fire({
//                         icon: 'error',
//                         text: resp.message,
//                         showConfirmButton: true
//                     })
//                 }

//                 setUrlVideo((prev) => {
//                     const items = [...prev]
//                     items[index] = _urlVideo
//                     return items
//                 });

//                 setConsultandoCamara((prev) => {
//                     const items = [...prev]
//                     items[index] = false
//                     return items
//                 })
//             })
//         } else {

//             setUrlVideo((prev) => {
//                 const items = [...prev]
//                 items[index] = _urlVideo
//                 return items
//             });
//         }

//         //console.log(urlVideo);

//         setVideoChecks((prev) => {
//             const newChecks = [...prev];
//             newChecks[index] = !newChecks[index];
//             return newChecks;
//         });
//     };

//     const titulo = [{ A: 'Reporte de Alertas' }, {}]

//     const longitudes = []

//     const creandoArchivo = (dataFinal) => {

//         const libro = XLSX.utils.book_new();

//         const hoja = XLSX.utils.json_to_sheet(dataFinal, { skipHeader: true });

//         hoja["!merges"] = [
//             XLSX.utils.decode_range("A1:F1"),
//             XLSX.utils.decode_range("A2:F2"),
//             //XLSX.utils.decode_range(`A${_totalFilas}:E${_totalFilas}`),
//         ];

//         let propiedades = [];

//         longitudes.forEach((col) => {
//             propiedades.push({
//                 width: col,
//             });
//         });

//         hoja["!cols"] = propiedades;

//         XLSX.utils.book_append_sheet(libro, hoja, "Alertas");

//         XLSX.writeFile(libro, "ReporteDeAlertas.xlsx");
//     };

//     const exportarDatos = () => {

//         let tabla = [
//             {
//                 A: 'Fecha',
//                 B: 'Tipo',
//                 C: 'Alerta',
//                 D: 'Vehiculo',
//                 E: 'Chofer',
//                 F: 'Ubicación'
//             }
//         ]

//         safeTableData.forEach((i) => {

//             tabla.push({
//                 A: formatFecha(i.fecha),
//                 B: i.alerta || "",
//                 C: i.distancia || "",
//                 D: i.vehiculos.capacidadtanque0litros || "0",
//                 E: texto
//             })
//         })

//         const dataFinal = [...titulo, ...tabla]

//         setTimeout(() => {
//             creandoArchivo(dataFinal);
//             //setLoading(false);
//         }, 1000);
//     }

//     const handleExportar = () => {

//         exportarDatos()
//     }

//     // 📌 Estructura de columnas basada en el objeto `alert` de la API
//     const columns = [
//         {
//             accessorKey: 'fecha',  // 📌 Fecha de la alerta
//             header: 'Fecha',
//             cell: ({ row: { original } }) => (
//                 <span >
//                     {formatFecha(original.fecha)}
//                 </span>
//             ),
//             meta: { headerProps: { className: 'text-900' } }
//         },
//         {
//             accessorKey: 'alerta_real',  // 📌 alerta real - oculta, solo se deja para que se filtre en al busqueda
//             header: 'Alerta Real',
//             cell: ({ row: { original } }) => (
//                 <span >
//                     {original.alerta_real}
//                 </span>
//             ),
//         },
//         {
//             accessorKey: 'alerta',  // 📌 Mensaje de alerta real
//             header: 'Alerta',
//             cell: ({ row }) => {
//                 const alertaColor = row.original.color || '#888888';
//                 return (
//                     <div>
//                         <div className="d-flex align-items-center">
//                             {isGroup ? (
//                                 <div className="avatar avatar-xl me-3">
//                                     <div className="avatar-name rounded-circle"
//                                         style={{
//                                             backgroundColor: `${alertaColor}33`, // Fondo en tono más claro
//                                             color: alertaColor,
//                                             border: `2px solid ${alertaColor}`
//                                         }}  >
//                                         <span className={`fs-9`}>
//                                             {row.original.alerta_real?.[0] || ""}
//                                         </span>
//                                     </div>
//                                 </div>
//                             ) : (
//                                 <div className="avatar avatar-xl me-3">
//                                     <div className="avatar-name rounded-circle"
//                                         style={{
//                                             backgroundColor: `${alertaColor}33`, // Fondo en tono más claro
//                                             color: alertaColor,
//                                             border: `2px solid ${alertaColor}`
//                                         }}  >
//                                         <span className={`fs-9`}>
//                                             {row.original.alerta?.[0] || ""}
//                                         </span>
//                                     </div>
//                                 </div>
//                             )}
//                             {isGroup ? (<div> <strong>{row.original.alerta_real}</strong><br></br><span>{row.original.alerta}</span> </div>) : (<div> <strong>{row.original.alerta}</strong> </div>)}
//                         </div>
//                     </div>
//                 );
//             },
//             meta: { headerProps: { className: 'text-900' } }
//         },
//         {
//             accessorKey: 'no_economico',  // 📌 Número económico del vehículo
//             header: 'Vehículo',
//             meta: { headerProps: { className: 'text-900' } }
//         },
//         {
//             accessorKey: 'chofer',  // 📌 Nombre del chofer
//             header: 'Chofer',
//             meta: { headerProps: { className: 'text-900 text-left' } }
//         },
//         {
//             accessorKey: 'video',
//             header: 'Video',
//             cell: ({ row: { original } }) =>
//                 original.vehiculo_con_camara ? (
//                     original.camara_en_linea ?
//                         <FaPlayCircle
//                             size={24}
//                             className="text-danger cursor-pointer"
//                             onClick={() => handleShowVideoModal(original)}
//                             style={{ cursor: 'pointer' }}
//                         /> : <span className="text-muted">No disponible</span>
//                 ) : (
//                     <span className="text-muted">N/A</span>
//                 ),
//             meta: { headerProps: { className: 'text-900 text-center' }, cellProps: { className: 'text-center' } }
//         },
//         // {
//         //     accessorKey: 'longitud',  // 📌 Longitud
//         //     header: 'Longitud',
//         //     meta: { headerProps: { className: 'text-900' } }
//         // },
//         {
//             accessorKey: 'geocerca',  // 📌 Geocerca
//             header: 'Geocerca',
//             cell: ({ row: { original } }) => original.geocerca || 'N/A',
//             meta: { headerProps: { className: 'text-900' } }
//         },
//         {
//             accessorKey: 'mapa',  // 📌 Mapa (icono)
//             header: 'Ubicación',
//             cell: ({ row: { original } }) => (
//                 <Button className="btn btn-light btn-sm rounded-5" onClick={() => handleShowMap(original.latitud, original.longitud)}>
//                     <FaMapMarkerAlt className="text-danger" />
//                 </Button>
//             ),
//             meta: { headerProps: { className: 'text-900 text-center' }, cellProps: { className: 'text-center' } }
//         }
//     ];

//     const limpiarModal = () => {

//         setShowVideoModal(false)
//         setVideoChecks([false, false, false, false])
//     }

//     const table = useAdvanceTable({
//         data: safeTableData,  // 🔹 Ahora usa `safeTableData` para evitar errores
//         columns,
//         sortable: true,
//         pagination: true,
//         perPage,
//         initialState: {
//             sorting: [
//                 {
//                     id: 'fecha',
//                     desc: false
//                 }
//             ],
//             columnVisibility: {
//                 alerta_real: false
//             }
//         }
//     });

//     return (
//         <GoogleMapsProvider>
//             <AdvanceTableProvider {...table}>
//                 <Card className="h-100" >
//                     <Card.Header>
//                         <Row className="d-flex align-items-center justify-content-between">
//                             <Col md={6}>
//                                 <h6 className="mb-0 text-nowrap py-2 py-xl-2">{title}</h6>
//                             </Col>
//                             <Col md={6} className="d-flex justify-content-end">
//                                 <AdvanceTableSearchBox
//                                     className="input-search-width pr-2 flex-grow-1"
//                                     placeholder="Buscar recorridos..."
//                                 />
//                                 <Button
//                                     variant="outline-secondary"
//                                     size="sm"
//                                     className="me-3 ms-3"
//                                 //onClick={handleExportar}
//                                 >
//                                     <FontAwesomeIcon icon={faExternalLinkAlt} /> Exportar
//                                 </Button>
//                             </Col>
//                         </Row>
//                     </Card.Header>
//                     <Card.Body className="p-0">
//                         {/**
//                         <AdvanceTable
//                             headerClassName="bg-200 text-nowrap align-middle"
//                             rowClassName="align-middle white-space-nowrap"
//                             tableProps={{ className: 'fs-10 mb-0 overflow-hidden' }}
//                         />
//                          */}
//                         <AdvanceTableCustom
//                             headerClassName="sticky-top bg-200 text-nowrap align-middle"
//                             rowClassName="fs-10 align-middle white-space-nowrap"
//                             tableProps={{ className: 'fs-10 mb-0 overflow-hidden' }}
//                         />
//                     </Card.Body>
//                     <Card.Footer>
//                         <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection rowsPerPageOptions={[100, 200, 300]} />
//                     </Card.Footer>
//                 </Card>

//             </AdvanceTableProvider>
//             {/* 🔹 Modal dividido en dos secciones */}
//             <Modal show={showMapModal} onHide={() => setShowMapModal(false)} size="lg" centered>
//                 <Modal.Header closeButton>
//                     <Modal.Title>Ubicación del Evento</Modal.Title>
//                 </Modal.Header>
//                 <Modal.Body>
//                     <Row>
//                         {/* Sección izquierda - Mapa con marcador */}
//                         <Col md={12}>
//                             {selectedLocation.lat && selectedLocation.lng ? (
//                                 <GoogleMapComponent
//                                     initialCenter={{
//                                         lat: selectedLocation.lat,
//                                         lng: selectedLocation.lng,
//                                     }}
//                                     mapStyle="hoper"
//                                     className="vh-50"
//                                     showStreetView={false}
//                                 />
//                             ) : (
//                                 <p>Ubicación no disponible</p>
//                             )}
//                         </Col>
//                     </Row>
//                 </Modal.Body>
//             </Modal>
//             {/* Modal para reproducción de video */}
//             <Modal show={showVideoModal} onHide={() => limpiarModal()} size="lg" centered>
//                 <Modal.Header closeButton>
//                     <Modal.Title>
//                         <h5>No Económico: {selectedVehicle?.no_economico} </h5>
//                         <h6>Fecha: {formatFecha(selectedVehicle?.fecha)} </h6>
//                     </Modal.Title>
//                 </Modal.Header>
//                 <Modal.Body>
//                     {/* Checkboxes en la parte superior */}
//                     <Row className="mb-3 d-flex justify-content-center">
//                         {videoIds.map((id, index) => (
//                             <Col xs="auto" key={id} className="text-center">
//                                 <Form.Check
//                                     id={'chk-' + id}
//                                     type="checkbox"
//                                     label={`Canal ${index + 1}`}
//                                     checked={videoChecks[index]}
//                                     onChange={(event) => handleToggleVideo(event, index)}
//                                     className="fw-bold"
//                                 />
//                             </Col>
//                         ))}
//                     </Row>
//                     {/* Cuadrícula de videos */}
//                     <Row>
//                         {videoIds.map((id, index) => (
//                             <Col md={6} key={id} className="mb-3 d-flex justify-content-center">

//                                 <div
//                                     id={`div-` + id}
//                                     className={consultandoCamara[index] ? `border p-2 text-center w-100 d-flex justify-content-center align-items-center` : `border p-2 text-center w-100`}
//                                     style={{ height: '200px' }}
//                                 >
//                                     {consultandoCamara[index] ? <Spinner animation="border" role="status"></Spinner> : ''}
//                                     {videoChecks[index] ? (
//                                         <>
//                                             <CamaraVisor
//                                                 id={`vid-` + id}
//                                                 vehiculo={selectedVehicle}
//                                                 checked={videoChecks[index]}
//                                                 urlVideo={urlVideo[index]}
//                                             >
//                                             </CamaraVisor>
//                                         </>
//                                     ) : (
//                                         ''
//                                     )}
//                                 </div>

//                             </Col>
//                         ))}
//                     </Row>
//                 </Modal.Body>
//             </Modal>
//         </GoogleMapsProvider>
//     );
// };

// AlertaTableList.propTypes = {
//     tableData: PropTypes.arrayOf(
//         PropTypes.shape({
//             no_economico: PropTypes.string.isRequired,
//             alerta_real: PropTypes.string.isRequired,
//             fecha: PropTypes.string.isRequired,
//             latitud: PropTypes.string,
//             longitud: PropTypes.string,
//             geocerca: PropTypes.string,
//             chofer: PropTypes.string
//         })
//     ),
//     perPage: PropTypes.number,
//     title: PropTypes.string,
//     isGroup: PropTypes.bool
// };

// export default AlertaTableList;

